import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type DependencyList,
    type RefObject,
} from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
    prioritizeVirtualItemsForViewport,
    resolveRetainedVirtualTotalSize,
    resolveVirtualItemStartCorrection,
    resolveVirtualItemViewportCorrection,
    resolveVirtualStartCorrection,
    resolveVirtualScrollMargin,
    shouldAdjustVirtualScrollForMeasuredSizeChange,
    shouldRetainVirtualStartCorrectionsForTotalSize,
    shouldUpdateVirtualScrollMargin,
} from 'lib/virtualScrollAdjustment'

const EMPTY_DEPS: DependencyList = []
const EMPTY_REFS: Array<RefObject<Element | null>> = []
const VIRTUAL_SCROLL_MEASUREMENT_IDLE_MS = 2000

type UsePanelVirtualizerOptions = {
    count: number
    enabled?: boolean
    estimateSize: (index: number) => number
    getItemKey: (index: number) => number | string | bigint
    resolveScrollElement: () => HTMLElement | null
    overscan?: number
    gap?: number
    measureDeps?: DependencyList
    scrollMarginDeps?: DependencyList
    observeElementRefs?: Array<RefObject<Element | null>>
    allowMeasuredShrinkAdjustment?: boolean
    retainMeasuredScrollMovement?: boolean
    retainBackwardMeasuredScrollMovement?: boolean
    retainVirtualStartMovement?: boolean
    retainBackwardVirtualStartOnTotalSizeChange?: boolean
    retainVirtualStartOnTotalSizeChange?: boolean
    staticGeometry?: boolean
    // Immutable row model for static geometry. Producers replace this array only when row
    // identity or geometry inputs change, keeping reconciliation out of scroll-driven renders.
    staticRows?: ReadonlyArray<{
        key: number | string | bigint
        estimatedHeight: number
        staticSignature: string | null
        resizeIntentSignature: string | null
    }>
}

type ScrollMetrics = {
    scrollTop: number
    scrollHeight: number
    clientHeight: number
}

function readScrollMetrics(scrollElement?: HTMLElement | null): ScrollMetrics | null {
    if (!scrollElement) return null

    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    if (
        !Number.isFinite(scrollTop) ||
        !Number.isFinite(scrollHeight) ||
        !Number.isFinite(clientHeight)
    ) {
        return null
    }

    return { scrollTop, scrollHeight, clientHeight }
}

const normalizeVirtualSize = (value: unknown) => {
    const size = Math.ceil(Number(value))
    return Number.isFinite(size) && size > 0 ? size : 1
}

const STATIC_GEOMETRY_DATASET_KEYS = [
    'virtualStaticGeometryInvariant',
    'virtualStaticCacheComplete',
    'virtualStaticLiveCount',
    'virtualStaticCachedCount',
    'virtualStaticFallbackMisses',
    'virtualStaticPrefillMisses',
    'virtualStaticPendingResizes',
] as const

type StaticSizeCacheEntry = {
    size: number
    signature: string | null
    resizeIntentSignature: string | null
}

type StaticPendingResize = {
    size: number
    immediate: boolean
}

// Rows fully below the rendered range never move the viewport when resized; everything else
// waits for scroll-idle so visible geometry is immutable during active scrolling.
const STATIC_RESIZE_IDLE_RETRY_MS = 200
// Anchored rowset compensation is skipped when the user is pinned to the top of the feed —
// there, prepend push-down is the expected product behavior.
const STATIC_ROWSET_ANCHOR_MIN_SCROLL_TOP_PX = 8

export function usePanelVirtualizer({
    count,
    enabled = true,
    estimateSize,
    getItemKey,
    resolveScrollElement,
    overscan,
    gap,
    measureDeps = EMPTY_DEPS,
    scrollMarginDeps = EMPTY_DEPS,
    observeElementRefs = EMPTY_REFS,
    allowMeasuredShrinkAdjustment = true,
    retainMeasuredScrollMovement = true,
    retainBackwardMeasuredScrollMovement = false,
    retainVirtualStartMovement = true,
    retainBackwardVirtualStartOnTotalSizeChange = false,
    retainVirtualStartOnTotalSizeChange = false,
    staticGeometry = false,
    staticRows,
}: UsePanelVirtualizerOptions) {
    const listElementRef = useRef<HTMLDivElement | null>(null)
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)
    const scrollMetricsRef = useRef<ScrollMetrics | null>(null)
    const flushPendingMeasurementsRef = useRef<(() => void) | null>(null)
    const queueMeasurementRef = useRef<((element: HTMLElement) => void) | null>(null)
    const retainScrollMovementRef = useRef(false)
    const scrollIdleTimerRef = useRef<number | null>(null)
    const lastScrollDirectionRef = useRef<'forward' | 'backward' | null>(null)
    const [isScrollMovementRetained, setIsScrollMovementRetained] = useState(false)
    const retainedTotalSizeRef = useRef({
        identityKey: '',
        rawTotalSize: 0,
        totalSize: 0,
    })
    const retainedVirtualStartsRef = useRef<{
        scrollTop: number | null
        starts: Map<string, number>
    }>({
        scrollTop: null,
        starts: new Map(),
    })
    const staticSizeCacheRef = useRef(new Map<string, StaticSizeCacheEntry>())
    const staticFallbackMissesRef = useRef(0)
    const staticPrefillMissesRef = useRef(0)
    // key → target size for signature-invalidated rows awaiting an in-place resizeItem commit.
    const staticPendingResizesRef = useRef(new Map<string, StaticPendingResize>())
    const staticKeyIndexRef = useRef(new Map<string, number>())
    const staticResizeTimerRef = useRef<number | null>(null)
    const staticSuppressAdjustmentRef = useRef(false)
    // Physical scrollTop captured once per flush batch. React Virtual's scrollOffset only
    // refreshes on the async scroll event while resizeItem moves scrollTop synchronously, so
    // classifying a batch against live library state drifts by the accumulated adjustments;
    // the frozen physical top is exact and order-independent (and also covers the rowset-
    // anchor pass's direct scrollTop write in the same commit).
    const staticFlushViewportTopRef = useRef<number | null>(null)
    // Keys freshly (re-)added to the wrapper cache this render. React Virtual's own
    // itemSizeCache persists per key across rowset changes, so a row that was resized, left
    // the rowset, and returned would keep its stale resized size while the wrapper cache
    // re-estimated — these are reconciled through the pending-resize policy.
    const staticRehydratedKeysRef = useRef<string[]>([])
    // Committed key/size order from the previous render, used to anchor the viewport across
    // rowset mutations (prepends, removals, reorders) that static mode never compensated.
    const staticOrderedRowsRef = useRef<Array<{ key: string; size: number }>>([])
    const staticOrderedRowsModelRef = useRef<Array<{ key: string; size: number }>>([])
    const staticRowsIdentityRef = useRef<typeof staticRows>(undefined)
    const staticGeometryWasEnabledRef = useRef(false)
    const staticLiveCountRef = useRef(0)

    useLayoutEffect(() => {
        const nextScrollElement = enabled ? resolveScrollElement?.() || null : null
        setScrollElement(current => (current === nextScrollElement ? current : nextScrollElement))
    })

    const getScrollElement = useCallback(() => scrollElement, [scrollElement])
    const [scrollMargin, setScrollMargin] = useState(0)
    const updateScrollMargin = useCallback(() => {
        const nextMargin = resolveVirtualScrollMargin({
            scrollElement,
            listElement: listElementRef.current,
        })

        setScrollMargin(current =>
            shouldUpdateVirtualScrollMargin({ currentMargin: current, nextMargin })
                ? Number(nextMargin)
                : current,
        )
    }, [scrollElement])

    const identityKey = (() => {
        if (!enabled || count <= 0) return ''
        const firstKey = staticGeometry ? staticRows?.[0]?.key : getItemKey(0)
        const lastKey = staticGeometry ? staticRows?.[count - 1]?.key : getItemKey(count - 1)
        return `${count}:${String(firstKey ?? '')}:${String(lastKey ?? '')}`
    })()
    const staticResizeChannelEnabled = enabled && staticGeometry && staticRows != null
    const staticRowModelChanged =
        staticResizeChannelEnabled &&
        (!staticGeometryWasEnabledRef.current || staticRowsIdentityRef.current !== staticRows)

    if (staticRowModelChanged) {
        const liveKeys = new Set<string>()
        const keyIndex = new Map<string, number>()
        const orderedRows: Array<{ key: string; size: number }> = []

        ;(staticRows || []).forEach((row, index) => {
            const key = String(row.key)
            const signature = row.staticSignature ?? null
            const resizeIntentSignature = row.resizeIntentSignature ?? null
            const estimatedSize = normalizeVirtualSize(row.estimatedHeight)
            liveKeys.add(key)
            keyIndex.set(key, index)

            const entry = staticSizeCacheRef.current.get(key)
            if (!entry) {
                staticSizeCacheRef.current.set(key, {
                    size: estimatedSize,
                    signature,
                    resizeIntentSignature,
                })
                staticPrefillMissesRef.current += 1
                staticRehydratedKeysRef.current.push(key)
                orderedRows.push({ key, size: estimatedSize })
                return
            }

            if (entry.signature !== signature) {
                const immediateResize =
                    (entry.resizeIntentSignature ?? null) !== resizeIntentSignature
                entry.signature = signature
                entry.resizeIntentSignature = resizeIntentSignature
                // The committed entry keeps the size React Virtual is laying out with; the
                // pending map carries the target until the position-aware policy applies it.
                if (estimatedSize !== entry.size) {
                    const currentPending = staticPendingResizesRef.current.get(key)
                    staticPendingResizesRef.current.set(key, {
                        size: estimatedSize,
                        immediate: immediateResize || currentPending?.immediate || false,
                    })
                } else {
                    staticPendingResizesRef.current.delete(key)
                }
            } else if ((entry.resizeIntentSignature ?? null) !== resizeIntentSignature) {
                entry.resizeIntentSignature = resizeIntentSignature
            }
            orderedRows.push({ key, size: entry.size })
        })

        staticSizeCacheRef.current.forEach((_entry, key) => {
            if (!liveKeys.has(key)) {
                staticSizeCacheRef.current.delete(key)
                staticPendingResizesRef.current.delete(key)
            }
        })
        staticKeyIndexRef.current = keyIndex
        staticOrderedRowsModelRef.current = orderedRows
        staticLiveCountRef.current = liveKeys.size
        staticRowsIdentityRef.current = staticRows
        staticGeometryWasEnabledRef.current = true
    } else if (!staticResizeChannelEnabled && staticGeometryWasEnabledRef.current) {
        staticSizeCacheRef.current.clear()
        staticPendingResizesRef.current.clear()
        staticKeyIndexRef.current = new Map()
        staticOrderedRowsModelRef.current = []
        staticRowsIdentityRef.current = undefined
        staticLiveCountRef.current = 0
        staticRehydratedKeysRef.current = []
        staticFallbackMissesRef.current = 0
        staticPrefillMissesRef.current = 0
        staticGeometryWasEnabledRef.current = false
    }
    const staticOrderedRows = staticOrderedRowsModelRef.current
    const staticLiveCount = staticGeometry ? staticLiveCountRef.current : 0
    const staticCachedCount = staticGeometry ? staticSizeCacheRef.current.size : 0
    const staticCacheComplete = !staticGeometry || staticCachedCount === staticLiveCount
    const staticEstimateSize = useCallback(
        (index: number) => {
            if (!staticGeometry) return estimateSize(index)

            const row = staticRows?.[index]
            const key = String(row?.key ?? getItemKey(index))
            const entry = staticSizeCacheRef.current.get(key)
            if (entry) return entry.size

            staticFallbackMissesRef.current += 1
            const size = normalizeVirtualSize(row?.estimatedHeight ?? estimateSize(index))
            staticSizeCacheRef.current.set(key, {
                size,
                signature: row?.staticSignature ?? null,
                resizeIntentSignature: row?.resizeIntentSignature ?? null,
            })
            // Fallback-created entries are rehydrations too: React Virtual may still hold a
            // stale resized size for this key from before it left the rowset.
            if (staticResizeChannelEnabled) staticRehydratedKeysRef.current.push(key)
            return size
        },
        [estimateSize, getItemKey, staticGeometry, staticResizeChannelEnabled, staticRows],
    )

    const virtualizer = useVirtualizer({
        count,
        getScrollElement,
        // The panel scroller ref can become available after React Virtual's
        // first layout pass. Read the real offset on attach so the library
        // does not replay its default 0 offset over an in-progress user scroll.
        initialOffset: () => getScrollElement()?.scrollTop ?? 0,
        enabled: enabled && !!scrollElement,
        getItemKey,
        estimateSize: staticEstimateSize,
        overscan,
        gap,
        scrollMargin,
    })

    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, delta, instance) =>
        staticGeometry
            ? // The v2 resize channel compensates rows that end above the viewport so content
              // in view never moves; rows at or below the viewport apply freely. The viewport
              // top is the flush batch's frozen physical scrollTop when one is in progress
              // (the library's scrollOffset goes stale mid-batch while resizeItem moves
              // scrollTop synchronously), else the live offset. Bookkeeping reconciles
              // suppress compensation entirely: the rowset-anchor pass already moved
              // scrollTop for their delta. Legacy static mode never resizes.
              staticResizeChannelEnabled &&
              !staticSuppressAdjustmentRef.current &&
              item.end <= (staticFlushViewportTopRef.current ?? instance.scrollOffset ?? 0)
            : shouldAdjustVirtualScrollForMeasuredSizeChange({
                  itemStart: item.start,
                  itemEnd: item.end,
                  delta,
                  scrollOffset: instance.scrollOffset ?? 0,
                  scrollAdjustment:
                      (instance as unknown as { scrollAdjustments?: number }).scrollAdjustments ??
                      0,
                  scrollDirection: instance.scrollDirection,
                  hasMeasuredSize: instance.itemSizeCache.has(item.key),
                  allowMeasuredShrinkAdjustment,
              })

    const virtualizerRef = useRef(virtualizer)
    virtualizerRef.current = virtualizer
    const measuredVirtualItems = virtualizer.getVirtualItems()
    // Item starts are absolute to the scroll container and include scrollMargin. Normalize
    // against the same measurement snapshot rather than the mutable option: a natural-flow
    // prefix can move the list before React Virtual rebuilds starts for the new margin.
    const virtualListStart =
        count > 0 ? virtualizer.measurementsCache[0]?.start ?? scrollMargin : scrollMargin
    const virtualTotalSize = virtualizer.getTotalSize()
    const shouldUseMeasuredGeometry = enabled && !staticGeometry
    const isMeasuredScrollMovementActive =
        shouldUseMeasuredGeometry &&
        (virtualizer.isScrolling || isScrollMovementRetained || retainScrollMovementRef.current)
    const shouldRetainVirtualStartMovement =
        retainVirtualStartMovement && isMeasuredScrollMovementActive
    const previousVirtualStarts = retainedVirtualStartsRef.current
    const previousRetainedTotalSize = retainedTotalSizeRef.current
    const virtualizerScrollAdjustment = Number(
        (virtualizer as unknown as { scrollAdjustments?: number }).scrollAdjustments ?? 0,
    )
    const hasVirtualizerScrollAdjustment =
        Number.isFinite(virtualizerScrollAdjustment) && Math.abs(virtualizerScrollAdjustment) > 2
    const currentScrollMetrics = shouldUseMeasuredGeometry
        ? scrollMetricsRef.current || readScrollMetrics(scrollElement)
        : null
    const totalSizeIsStableForStartCorrection = shouldRetainVirtualStartCorrectionsForTotalSize({
        totalSize: virtualTotalSize,
        previousTotalSize: previousRetainedTotalSize.rawTotalSize,
    })
    const observedScrollDirection = (() => {
        const previousScrollTop = Number(previousVirtualStarts.scrollTop)
        const currentScrollTop = Number(currentScrollMetrics?.scrollTop)
        if (Number.isFinite(previousScrollTop) && Number.isFinite(currentScrollTop)) {
            if (currentScrollTop < previousScrollTop - 2) return 'backward'
            if (currentScrollTop > previousScrollTop + 2) return 'forward'

            return null
        }

        return virtualizer.scrollDirection
    })()
    const isBackwardScrollMovement =
        observedScrollDirection === 'backward' ||
        virtualizer.scrollDirection === 'backward' ||
        lastScrollDirectionRef.current === 'backward'
    const shouldRetainScrollMovement =
        isMeasuredScrollMovementActive &&
        (retainMeasuredScrollMovement ||
            (retainBackwardMeasuredScrollMovement && isBackwardScrollMovement))
    const totalSizeCanUseStartCorrection = shouldRetainVirtualStartCorrectionsForTotalSize({
        totalSize: virtualTotalSize,
        previousTotalSize: previousRetainedTotalSize.rawTotalSize,
        retainOnTotalSizeChange: retainVirtualStartOnTotalSizeChange,
        retainOnBackwardTotalSizeChange: retainBackwardVirtualStartOnTotalSizeChange,
        scrollDirection: isBackwardScrollMovement ? 'backward' : observedScrollDirection,
        hasVirtualizerScrollAdjustment,
    })
    const virtualStartCorrectionItems = prioritizeVirtualItemsForViewport({
        items: measuredVirtualItems,
        scrollTop: currentScrollMetrics?.scrollTop,
        clientHeight: currentScrollMetrics?.clientHeight,
    })
    // Keep visible rows anchored as a group while queued/late measurements above
    // the viewport change React Virtual's item starts during active scroll.
    // Once scrolling has settled, release meaningful corrections so stale starts
    // cannot move visible rows during an idle layout pass.
    // Meaningful total-size changes are anchored by React Virtual itself only
    // when it publishes a scroll adjustment. Otherwise visible row starts still
    // need the same active-scroll correction as size-neutral row-start drift.
    // Unlike total-size retention, start retention can survive page-window,
    // append, and tail-key changes because existing row starts are comparable
    // by item key.
    const shouldCorrectVirtualStarts =
        shouldUseMeasuredGeometry &&
        totalSizeCanUseStartCorrection &&
        previousVirtualStarts.starts.size > 0
    const virtualStartCorrection = shouldCorrectVirtualStarts
        ? resolveVirtualStartCorrection({
              items: virtualStartCorrectionItems,
              retainedStarts: previousVirtualStarts.starts,
              retainScrollMovement: shouldRetainVirtualStartMovement,
              retainUpwardMovement: true,
          })
        : 0
    const sharedVirtualItems =
        virtualStartCorrection === 0
            ? measuredVirtualItems
            : measuredVirtualItems.map(item => ({
                  ...item,
                  start: item.start + virtualStartCorrection,
                  end: item.end + virtualStartCorrection,
              }))
    // After the shared anchor is applied, React Virtual can still publish a
    // tiny per-row start delta from rounded measurements. Clamp only that
    // residual noise even when total-size changes are using React Virtual's
    // own anchoring path.
    const virtualItems =
        shouldUseMeasuredGeometry && previousVirtualStarts.starts.size > 0
            ? sharedVirtualItems.map(item => {
                  const correction = totalSizeIsStableForStartCorrection
                      ? resolveVirtualItemStartCorrection({
                            item,
                            retainedStarts: previousVirtualStarts.starts,
                            retainScrollMovement: false,
                        })
                      : resolveVirtualItemViewportCorrection({
                            item,
                            retainedStarts: previousVirtualStarts.starts,
                            retainedScrollTop: previousVirtualStarts.scrollTop,
                            scrollTop: currentScrollMetrics?.scrollTop,
                        })

                  return correction === 0
                      ? item
                      : {
                            ...item,
                            start: item.start + correction,
                            end: item.end + correction,
                        }
              })
            : sharedVirtualItems
    const nextRetainedVirtualStarts = shouldUseMeasuredGeometry
        ? {
              scrollTop: currentScrollMetrics?.scrollTop ?? null,
              starts: new Map(virtualItems.map(item => [String(item.key), item.start])),
          }
        : {
              scrollTop: null,
              starts: new Map(),
          }

    useLayoutEffect(() => {
        retainedVirtualStartsRef.current = nextRetainedVirtualStarts
    })
    const shouldRetainTotalSize =
        shouldUseMeasuredGeometry &&
        enabled &&
        count > 0 &&
        previousRetainedTotalSize.identityKey === identityKey
    const scrollMetrics = scrollMetricsRef.current
    const totalSize = shouldRetainTotalSize
        ? resolveRetainedVirtualTotalSize({
              totalSize: virtualTotalSize,
              retainedTotalSize: previousRetainedTotalSize.totalSize,
              scrollTop: scrollMetrics?.scrollTop,
              scrollHeight: scrollMetrics?.scrollHeight,
              clientHeight: scrollMetrics?.clientHeight,
              isScrolling: shouldRetainScrollMovement,
              retainGrowth: shouldRetainScrollMovement && isBackwardScrollMovement,
          })
        : virtualTotalSize

    retainedTotalSizeRef.current = shouldUseMeasuredGeometry
        ? {
              identityKey,
              rawTotalSize: virtualTotalSize,
              totalSize,
          }
        : {
              identityKey: '',
              rawTotalSize: 0,
              totalSize: 0,
          }

    useLayoutEffect(() => {
        scrollMetricsRef.current = shouldUseMeasuredGeometry
            ? readScrollMetrics(scrollElement)
            : null
    })

    useLayoutEffect(() => {
        const element = listElementRef.current
        if (!element) return

        if (!staticGeometry) {
            STATIC_GEOMETRY_DATASET_KEYS.forEach(key => {
                delete element.dataset[key]
            })
            return
        }

        const fallbackMisses = staticFallbackMissesRef.current
        const invariantHolds = staticCacheComplete && fallbackMisses === 0
        element.dataset.virtualStaticGeometryInvariant = invariantHolds ? 'true' : 'false'
        element.dataset.virtualStaticCacheComplete = staticCacheComplete ? 'true' : 'false'
        element.dataset.virtualStaticLiveCount = String(staticLiveCount)
        element.dataset.virtualStaticCachedCount = String(staticCachedCount)
        element.dataset.virtualStaticFallbackMisses = String(fallbackMisses)
        element.dataset.virtualStaticPrefillMisses = String(staticPrefillMissesRef.current)
        element.dataset.virtualStaticPendingResizes = String(staticPendingResizesRef.current.size)
    })

    // V2 size-invalidation channel: anchor the viewport across rowset mutations, then commit
    // signature-invalidated reserves in place. Both run before paint so no intermediate frame
    // is visible.
    useLayoutEffect(() => {
        const previousRows = staticOrderedRowsRef.current
        staticOrderedRowsRef.current = staticOrderedRows

        if (!staticResizeChannelEnabled) return
        if (previousRows === staticOrderedRows) return
        if (previousRows.length === 0 || staticOrderedRows.length === 0) return

        const rowsetChanged =
            previousRows.length !== staticOrderedRows.length ||
            previousRows.some((row, ix) => row.key !== staticOrderedRows[ix]?.key)
        if (!rowsetChanged) return

        const scrollEl = getScrollElement()
        if (!scrollEl) return
        const scrollTop = scrollEl.scrollTop
        if (scrollTop <= STATIC_ROWSET_ANCHOR_MIN_SCROLL_TOP_PX) return

        const rowGap = Number(gap) || 0
        const nextStarts = new Map<string, number>()
        let nextOffset = 0
        staticOrderedRows.forEach(row => {
            nextStarts.set(row.key, nextOffset)
            nextOffset += row.size + rowGap
        })

        // Anchor on the first surviving row at or below the previous viewport top: its list
        // offset before and after the mutation tells us exactly how far the content the user
        // was looking at moved.
        const listScrollTop = scrollTop - (Number(scrollMargin) || 0)
        let prevOffset = 0
        let anchorKey: string | null = null
        let anchorPrevStart = 0
        let fallbackKey: string | null = null
        let fallbackPrevStart = 0
        for (let ix = 0; ix < previousRows.length; ix += 1) {
            const row = previousRows[ix]
            if (nextStarts.has(row.key)) {
                if (fallbackKey == null) {
                    fallbackKey = row.key
                    fallbackPrevStart = prevOffset
                }
                if (prevOffset + row.size > listScrollTop) {
                    anchorKey = row.key
                    anchorPrevStart = prevOffset
                    break
                }
            }
            prevOffset += row.size + rowGap
        }

        const resolvedKey = anchorKey ?? fallbackKey
        if (resolvedKey == null) return
        const resolvedPrevStart = anchorKey != null ? anchorPrevStart : fallbackPrevStart
        const nextStart = nextStarts.get(resolvedKey)
        if (nextStart == null) return

        const delta = nextStart - resolvedPrevStart
        if (Math.abs(delta) <= 1) return
        scrollEl.scrollTop = scrollTop + delta
    })

    const flushStaticPendingResizes = useCallback(() => {
        if (!staticResizeChannelEnabled) return

        const activeVirtualizer = virtualizerRef.current
        const pending = staticPendingResizesRef.current

        // Rehydration reconciles run IMMEDIATELY and never scroll-compensate: the rowset-
        // anchor pass in this same commit already moved scrollTop assuming the wrapper cache's
        // fresh sizes, so aligning React Virtual's stale per-key size pre-paint is what makes
        // that adjustment land on the right content — deferring (or compensating again) would
        // paint a one-frame (or double) shift.
        if (staticRehydratedKeysRef.current.length > 0) {
            const rehydratedKeys = staticRehydratedKeysRef.current
            staticRehydratedKeysRef.current = []
            rehydratedKeys.forEach(key => {
                const index = staticKeyIndexRef.current.get(key)
                const entry = staticSizeCacheRef.current.get(key)
                if (index == null || !entry) return

                const measuredSize = activeVirtualizer.itemSizeCache?.get?.(key)
                if (measuredSize != null && measuredSize !== entry.size) {
                    staticSuppressAdjustmentRef.current = true
                    try {
                        activeVirtualizer.resizeItem(index, entry.size)
                    } finally {
                        staticSuppressAdjustmentRef.current = false
                    }
                }
            })
        }

        if (pending.size === 0) return

        const renderedItems = activeVirtualizer.getVirtualItems()
        const lastRenderedIndex = renderedItems.length
            ? renderedItems[renderedItems.length - 1].index
            : null
        const isActivelyScrolling = activeVirtualizer.isScrolling || retainScrollMovementRef.current
        let deferred = false

        // Captured after the rehydration reconciles so the whole pending batch classifies
        // against one consistent viewport top regardless of apply order.
        staticFlushViewportTopRef.current = getScrollElement()?.scrollTop ?? null
        try {
            pending.forEach((resize, key) => {
                const index = staticKeyIndexRef.current.get(key)
                const entry = staticSizeCacheRef.current.get(key)
                if (index == null || !entry) {
                    pending.delete(key)
                    return
                }

                const isBelowRenderedRange = lastRenderedIndex != null && index > lastRenderedIndex
                if (isActivelyScrolling && !isBelowRenderedRange && !resize.immediate) {
                    deferred = true
                    return
                }

                entry.size = resize.size
                const orderedRow = staticOrderedRowsModelRef.current[index]
                if (orderedRow?.key === key) orderedRow.size = resize.size
                pending.delete(key)
                activeVirtualizer.resizeItem(index, resize.size)
            })
        } finally {
            staticFlushViewportTopRef.current = null
        }

        if (deferred && staticResizeTimerRef.current == null) {
            staticResizeTimerRef.current = window.setTimeout(() => {
                staticResizeTimerRef.current = null
                flushStaticPendingResizes()
            }, STATIC_RESIZE_IDLE_RETRY_MS)
        }
    }, [staticResizeChannelEnabled])

    useLayoutEffect(() => {
        flushStaticPendingResizes()
    })

    useEffect(
        () => () => {
            if (staticResizeTimerRef.current != null) {
                window.clearTimeout(staticResizeTimerRef.current)
                staticResizeTimerRef.current = null
            }
        },
        [],
    )

    const clearScrollIdleTimer = useCallback(() => {
        if (scrollIdleTimerRef.current == null) return
        window.clearTimeout(scrollIdleTimerRef.current)
        scrollIdleTimerRef.current = null
    }, [])

    const markScrollMovement = useCallback(() => {
        if (!retainScrollMovementRef.current) {
            retainScrollMovementRef.current = true
            setIsScrollMovementRetained(true)
        }
        clearScrollIdleTimer()
        scrollIdleTimerRef.current = window.setTimeout(() => {
            retainScrollMovementRef.current = false
            lastScrollDirectionRef.current = null
            scrollIdleTimerRef.current = null
            setIsScrollMovementRetained(false)
        }, VIRTUAL_SCROLL_MEASUREMENT_IDLE_MS)
    }, [clearScrollIdleTimer])

    useLayoutEffect(() => {
        if (!enabled || !scrollElement) {
            scrollMetricsRef.current = null
            return
        }

        if (staticGeometry) {
            scrollMetricsRef.current = null
            // Static resize commits still need to know when a user scroll burst is active, but
            // they do not need the measured-mode React state updates that rerender the list.
            const handleStaticScrollMovement = () => {
                retainScrollMovementRef.current = true
                clearScrollIdleTimer()
                scrollIdleTimerRef.current = window.setTimeout(() => {
                    retainScrollMovementRef.current = false
                    scrollIdleTimerRef.current = null
                }, VIRTUAL_SCROLL_MEASUREMENT_IDLE_MS)
            }

            scrollElement.addEventListener('scroll', handleStaticScrollMovement, { passive: true })
            scrollElement.addEventListener('wheel', handleStaticScrollMovement, { passive: true })
            scrollElement.addEventListener('touchmove', handleStaticScrollMovement, {
                passive: true,
            })

            return () => {
                scrollElement.removeEventListener('scroll', handleStaticScrollMovement)
                scrollElement.removeEventListener('wheel', handleStaticScrollMovement)
                scrollElement.removeEventListener('touchmove', handleStaticScrollMovement)
                clearScrollIdleTimer()
                retainScrollMovementRef.current = false
                lastScrollDirectionRef.current = null
            }
        }

        const updateScrollMetrics = () => {
            const previousScrollTop = Number(scrollMetricsRef.current?.scrollTop)
            const nextMetrics = readScrollMetrics(scrollElement)
            const nextScrollTop = Number(nextMetrics?.scrollTop)
            if (Number.isFinite(previousScrollTop) && Number.isFinite(nextScrollTop)) {
                if (nextScrollTop < previousScrollTop - 2) {
                    lastScrollDirectionRef.current = 'backward'
                } else if (nextScrollTop > previousScrollTop + 2) {
                    lastScrollDirectionRef.current = 'forward'
                }
            }
            scrollMetricsRef.current = nextMetrics
        }
        const handleScroll = () => {
            updateScrollMetrics()
            markScrollMovement()
        }

        updateScrollMetrics()
        scrollElement.addEventListener('scroll', handleScroll, { passive: true })
        scrollElement.addEventListener('wheel', markScrollMovement, { passive: true })

        let resizeObserver: ResizeObserver | null = null
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateScrollMetrics)
            resizeObserver.observe(scrollElement)
        }

        return () => {
            scrollElement.removeEventListener('scroll', handleScroll)
            scrollElement.removeEventListener('wheel', markScrollMovement)
            resizeObserver?.disconnect()
            clearScrollIdleTimer()
            retainScrollMovementRef.current = false
            lastScrollDirectionRef.current = null
            setIsScrollMovementRetained(false)
        }
    }, [clearScrollIdleTimer, enabled, markScrollMovement, scrollElement, staticGeometry])

    // React Virtual's ref measurement can miss rich rows that grow after mount.
    // Keep one local observer on mounted virtual rows, but only flush changed
    // rows after active scroll settles.
    useLayoutEffect(() => {
        if (staticGeometry) {
            flushPendingMeasurementsRef.current = null
            queueMeasurementRef.current = null
            return
        }
        if (!enabled || !scrollElement) return
        if (typeof ResizeObserver === 'undefined') return

        const root = listElementRef.current
        if (!root) return

        let frameId: number | null = null
        const pendingElements = new Set<HTMLElement>()
        const observedElements = new Set<HTMLElement>()

        const shouldDeferMeasurements = () =>
            virtualizerRef.current.isScrolling || retainScrollMovementRef.current

        const cancelScheduledFlush = () => {
            if (frameId == null) return

            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(frameId)
            } else {
                window.clearTimeout(frameId)
            }
            frameId = null
        }

        const flushMeasurements = () => {
            frameId = null
            if (shouldDeferMeasurements()) {
                return
            }

            pendingElements.forEach(element => {
                if (root.contains(element)) {
                    virtualizerRef.current.measureElement(element)
                }
            })
            pendingElements.clear()
        }

        const scheduleMeasure = (element: HTMLElement) => {
            pendingElements.add(element)
            if (shouldDeferMeasurements()) return

            cancelScheduledFlush()
            frameId =
                typeof window.requestAnimationFrame === 'function'
                    ? window.requestAnimationFrame(flushMeasurements)
                    : window.setTimeout(flushMeasurements, 0)
        }

        flushPendingMeasurementsRef.current = () => {
            cancelScheduledFlush()
            flushMeasurements()
        }
        queueMeasurementRef.current = scheduleMeasure

        const resizeObserver = new ResizeObserver(entries => {
            entries.forEach(entry => {
                if (entry.target instanceof HTMLElement) scheduleMeasure(entry.target)
            })
        })

        const observeMountedRows = () => {
            root.childNodes.forEach(node => {
                if (!(node instanceof HTMLElement)) return
                if (observedElements.has(node)) return

                observedElements.add(node)
                resizeObserver.observe(node)
            })
        }

        observeMountedRows()

        let mutationObserver: MutationObserver | null = null
        if (typeof MutationObserver !== 'undefined') {
            mutationObserver = new MutationObserver(observeMountedRows)
            mutationObserver.observe(root, { childList: true })
        }

        return () => {
            flushPendingMeasurementsRef.current = null
            queueMeasurementRef.current = null
            resizeObserver.disconnect()
            mutationObserver?.disconnect()
            pendingElements.clear()
            cancelScheduledFlush()
        }
    }, [enabled, scrollElement, staticGeometry])

    useEffect(() => {
        if (
            staticGeometry ||
            !enabled ||
            !scrollElement ||
            shouldRetainScrollMovement ||
            retainScrollMovementRef.current
        ) {
            return
        }
        flushPendingMeasurementsRef.current?.()
    }, [enabled, scrollElement, shouldRetainScrollMovement, staticGeometry])

    useLayoutEffect(() => {
        if (!enabled) return
        updateScrollMargin()
        const frameId = window.requestAnimationFrame?.(updateScrollMargin)

        return () => {
            if (frameId != null) window.cancelAnimationFrame?.(frameId)
        }
    }, [enabled, updateScrollMargin, ...scrollMarginDeps])

    useLayoutEffect(() => {
        if (!enabled) return
        if (typeof ResizeObserver === 'undefined') return

        let frameId: number | null = null
        const scheduleScrollMarginUpdate = () => {
            if (frameId != null) window.cancelAnimationFrame?.(frameId)
            frameId = window.requestAnimationFrame?.(() => {
                frameId = null
                updateScrollMargin()
            })
            if (frameId == null) updateScrollMargin()
        }

        const observer = new ResizeObserver(scheduleScrollMarginUpdate)
        ;[...observeElementRefs.map(ref => ref.current), listElementRef.current].forEach(node => {
            if (node) observer.observe(node)
        })

        return () => {
            observer.disconnect()
            if (frameId != null) window.cancelAnimationFrame?.(frameId)
        }
    }, [enabled, updateScrollMargin, ...observeElementRefs])

    useEffect(() => {
        if (!enabled) return
        if (staticGeometry) return
        virtualizerRef.current.measure()
    }, [enabled, staticGeometry, ...measureDeps])

    const measureElement = useCallback(
        (element: HTMLElement | null) => {
            if (!enabled || !element || staticGeometry) return

            if (virtualizerRef.current.isScrolling || retainScrollMovementRef.current) {
                const queueMeasurement = queueMeasurementRef.current
                if (queueMeasurement) {
                    queueMeasurement(element)
                    return
                }
            }

            virtualizerRef.current.measureElement(element)
        },
        [enabled, staticGeometry],
    )

    return {
        listElementRef,
        measureElement,
        scrollElement,
        totalSize,
        virtualizer,
        virtualListStart,
        virtualItems,
    }
}
